
import React from 'react';
import { usePublicCommunity, CommunityConfig } from './PublicCommunityContext';
import { useCommunityMember, UserRole } from './CommunityMemberContext';
import { Parameters } from '@communitly/shared';

// This is a compatibility hook to maintain backward compatibility with existing code
export interface CommunityContextType {
  // Community data (from PublicCommunityContext)
  currentCommunity: CommunityConfig;
  parameters: Parameters | null;
  getParameter: <T extends string | boolean | number>(key: string) => T | null;
  
  // User role in community (from CommunityMemberContext)
  userRole: UserRole;
  isUserInCommunity: boolean;
  
  // Loading states
  isLoading: boolean;
  error: string | null;
  
  // Refresh methods
  refreshCommunityData: () => Promise<void>;
  isAllowedTo: (permission: string) => Promise<boolean>;
}

// Re-export types for backward compatibility
export type { CommunityConfig };
export type { UserRole };
export { defaultCommunity } from './PublicCommunityContext';

// Combined hook that merges PublicCommunityContext and CommunityMemberContext
export const useCommunity = (): CommunityContextType => {
  const publicCommunity = usePublicCommunity();
  const communityMember = useCommunityMember();

  return {
    // From PublicCommunityContext
    currentCommunity: publicCommunity.currentCommunity,
    parameters: publicCommunity.parameters,
    getParameter: publicCommunity.getParameter,
    isLoading: publicCommunity.isLoading,
    error: publicCommunity.error || communityMember.error,
    refreshCommunityData: publicCommunity.refreshCommunityData,
    
    // From CommunityMemberContext
    userRole: communityMember.userRole,
    isUserInCommunity: communityMember.isUserInCommunity,
    isAllowedTo: communityMember.isAllowedTo,
  };
};
